import React from 'react'
import DateInput, { FormikDatePicker, PopperPlacementPositions } from 'particles/DatePicker'
import { useNewTranslation } from 'hooks/index'
import { BaseInputFieldProps } from '.'
import { RangeInputContainer } from './style'
import Text from 'particles/Text'
import CommentContainer from 'components/Shared/Comment'
import Label, { LabelSize } from 'particles/Label'
import IconButton from 'particles/Button/IconButton'
import { Country } from 'services/api/constants'

interface DateInputRangeProps extends BaseInputFieldProps {
    onClickClear?: (value: string | undefined) => void
    firstRangeOnChange?: (date: Date | null) => void
    secondRangeOnChange?: (date: Date | null) => void
    firstRangeDate?: string
    secondRangeDate?: string
    showTime?: boolean
    firstRangePopperPlacement?: PopperPlacementPositions
    secondRangePopperPlacement?: PopperPlacementPositions
    country?: Country
    isFormikComponent?: boolean
    labelSize?: LabelSize
}

const DateInputRange: React.FC<DateInputRangeProps> = ({
    label,
    labelSize,
    subtext,
    size = 'm',
    firstRangeTitle,
    firstRangeName,
    firstRangePlaceholder,
    firstRangeIdentifier,
    secondRangeTitle,
    secondRangeName,
    secondRangePlaceholder,
    secondRangeIdentifier,
    isDisabled = false,
    width,
    inputWidth,
    isReadOnly = false,
    isVertical = false,
    isRequired = false,
    isCommentable,
    isCommented,
    onClickClear,
    firstRangeOnChange,
    secondRangeOnChange,
    onClose,
    firstRangeDate,
    secondRangeDate,
    showTime,
    errorMessage,
    firstRangePopperPlacement,
    secondRangePopperPlacement,
    country,
    className,
    isFormikComponent,
    identifier,
}) => {
    const [newT] = useNewTranslation()

    const DateInputComponent = isFormikComponent ? FormikDatePicker : DateInput

    return (
        <RangeInputContainer className={className} isVertical={isVertical} width={width}>
            {label || subtext ? (
                <div style={{ display: 'flex' }}>
                    <CommentContainer identifier={identifier}>
                        <Label
                            title={label ?? ''}
                            subtext={subtext}
                            isRequired={isRequired}
                            size={labelSize || size}
                            isCommentable={isCommentable}
                            isCommented={isCommented}
                        />
                    </CommentContainer>
                    {onClose && !isReadOnly ? (
                        <IconButton icon="x" form="icon" size="xs" color="neutral" onClick={onClose} />
                    ) : null}
                </div>
            ) : null}
            <div className="range-input">
                <DateInputComponent
                    country={country}
                    label={firstRangeTitle}
                    name={firstRangeName as string}
                    placeholder={firstRangePlaceholder}
                    isDisabled={isDisabled}
                    width={inputWidth}
                    isReadOnly={isReadOnly}
                    onChange={firstRangeOnChange}
                    onClickClear={() => onClickClear?.(firstRangeName)}
                    date={firstRangeDate}
                    showTime={showTime}
                    showClearButton={!isDisabled && Boolean(firstRangeDate)}
                    size={size}
                    popperPlacement={firstRangePopperPlacement}
                    className="range-input-item"
                    identifier={firstRangeIdentifier}
                    showErrorText={false}
                    showError={!!errorMessage}
                />
                <Text scale="bodyS500" className="to-block">
                    {newT('general.information.toRangeFields')}
                </Text>
                <DateInputComponent
                    country={country}
                    label={secondRangeTitle}
                    name={secondRangeName as string}
                    placeholder={secondRangePlaceholder}
                    isDisabled={isDisabled}
                    width={inputWidth}
                    isReadOnly={isReadOnly}
                    onChange={secondRangeOnChange}
                    onClickClear={() => onClickClear?.(secondRangeName)}
                    date={secondRangeDate}
                    showTime={showTime}
                    showClearButton={!isDisabled && Boolean(secondRangeDate)}
                    size={size}
                    popperPlacement={secondRangePopperPlacement}
                    className="range-input-item"
                    identifier={secondRangeIdentifier}
                    showErrorText={false}
                    showError={!!errorMessage}
                />
            </div>
            {!isDisabled && errorMessage && (
                <Text scale="caption400" color="error">
                    {errorMessage}
                </Text>
            )}
        </RangeInputContainer>
    )
}
export default DateInputRange
